/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import PostMeetingEmailButton from '@/components/PostMeetingEmailButton';
import BulkPostMeetingEmail from '@/components/BulkPostMeetingEmail';
import {
  Plus, Upload, FileText, Trash2, Loader2, ExternalLink,
  Search, X, Pencil, Check, UserPlus, Users, ChevronDown, ChevronRight,
  FolderOpen, Folder, Mail, MailCheck, Sparkles,
} from 'lucide-react';

const API = '/api';

const emptyForm = (conference = '') => ({
  company_name: '',
  name: '',
  email: '',
  additional_emails: '',
  notes: '',
  conference,
});

const NEW_CONF = '__new__';

export default function MetInPersonPage() {
  const [rows, setRows] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [stats, setStats] = useState({ total: 0, matched: 0, unmatched: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | linked | unmatched
  const [loading, setLoading] = useState(true);

  // Which conference folders are expanded (by name). Default: all expanded.
  const [expanded, setExpanded] = useState({});
  // Inline rename state for a folder
  const [renamingConf, setRenamingConf] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameStart, setRenameStart] = useState('');
  const [bulkEmailFor, setBulkEmailFor] = useState(null);
  const [renameEnd, setRenameEnd] = useState('');

  // Manual-add form
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [newConfName, setNewConfName] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit-in-place
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  // File upload + review staging
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState(null); // { rows, total, matched, unmatched, conference }
  const fileInputRef = useRef(null);
  // Conference details modal that opens after a file is picked
  const [confDialog, setConfDialog] = useState({
    open: false, file: null, conference: '', start: '', end: '',
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('match_status', statusFilter);
      const res = await axios.get(`${API}/met?${params.toString()}`, { withCredentials: true });
      setRows(res.data.items || []);
      setConferences(res.data.conferences || []);
      setStats({
        total: res.data.total || 0,
        matched: res.data.matched || 0,
        unmatched: res.data.unmatched || 0,
      });
      // Expand all folders by default on first load
      setExpanded(prev => {
        const next = { ...prev };
        (res.data.conferences || []).forEach(c => {
          if (!(c.name in next)) next[c.name] = true;
        });
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, [statusFilter]);
  useEffect(() => {
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [search]);

  // Group rows by conference for rendering
  const grouped = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const k = r.conference || 'Uncategorized';
      (map[k] = map[k] || []).push(r);
    }
    return map;
  }, [rows]);

  const confNames = conferences.map(c => c.name);

  // ─────────── Manual add ───────────
  const submitAdd = async (e) => {
    e?.preventDefault?.();
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    // Resolve conference: either picked existing, or typed new
    let conference = form.conference;
    if (conference === NEW_CONF) conference = newConfName.trim();
    if (!conference) {
      toast.error('Pick or name a conference folder');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/met`, {
        company_name: form.company_name.trim(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        additional_emails: form.additional_emails
          .split(/[,;]/).map(s => s.trim()).filter(Boolean),
        notes: form.notes.trim(),
        conference,
      }, { withCredentials: true });
      toast.success(`Added to ${conference}`);
      setForm(emptyForm(conference));
      setNewConfName('');
      setShowAdd(false);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ─────────── Edit row ───────────
  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      company_name: row.company_name || '',
      name: row.name || '',
      email: row.email || '',
      additional_emails: (row.additional_emails || []).join(', '),
      notes: row.notes || '',
      conference: row.conference || '',
    });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/met/${id}`, {
        company_name: editForm.company_name.trim(),
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        additional_emails: editForm.additional_emails
          .split(/[,;]/).map(s => s.trim()).filter(Boolean),
        notes: editForm.notes.trim(),
      }, { withCredentials: true });
      toast.success('Updated');
      setEditingId(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteRow = async (id, label) => {
    if (!window.confirm(`Remove ${label || 'this contact'}?`)) return;
    try {
      await axios.delete(`${API}/met/${id}`, { withCredentials: true });
      toast.success('Removed');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ─────────── Folder actions ───────────
  const startRenameConf = (conf) => {
    setRenamingConf(conf.name);
    setRenameValue(conf.name);
    setRenameStart(conf.start_date || '');
    setRenameEnd(conf.end_date || '');
  };

  const submitRenameConf = async () => {
    const oldName = renamingConf;
    const newName = renameValue.trim();
    if (!newName) { setRenamingConf(null); return; }
    try {
      await axios.post(`${API}/met/conference/rename`, {
        old_name: oldName,
        new_name: newName,
        start_date: renameStart || '',
        end_date: renameEnd || '',
      }, { withCredentials: true });
      toast.success(newName !== oldName ? `Renamed to "${newName}"` : 'Folder updated');
      setRenamingConf(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const deleteConf = async (name, count) => {
    if (!window.confirm(`Delete the "${name}" folder and all ${count} contact${count === 1 ? '' : 's'} inside? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/met/conference/${encodeURIComponent(name)}`, { withCredentials: true });
      toast.success(`Deleted "${name}"`);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL ${stats.total} contacts across every conference? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/met`, { withCredentials: true });
      toast.success('All contacts cleared');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clear failed');
    }
  };

  // ─────────── File upload ───────────
  const onFilePicked = (file) => {
    if (!file) return;
    const ok = /\.(csv|xlsx|xlsm|xls|pdf)$/i.test(file.name);
    if (!ok) {
      toast.error('Upload a .csv, .xlsx, or .pdf file');
      return;
    }
    // Default conference name = filename without extension
    const defaultName = file.name.replace(/\.[^.]+$/, '').trim();
    setConfDialog({
      open: true,
      file,
      conference: defaultName,
      start: '',
      end: '',
    });
  };

  const cancelConfDialog = () => {
    setConfDialog({ open: false, file: null, conference: '', start: '', end: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitConfDialog = () => {
    const { file, conference, start, end } = confDialog;
    if (!file) { cancelConfDialog(); return; }
    const conf = (conference || '').trim() || file.name.replace(/\.[^.]+$/, '').trim();
    if (start && end && end < start) {
      toast.error('End date cannot be before start date');
      return;
    }
    setConfDialog(prev => ({ ...prev, open: false }));
    doUpload(file, conf, (start || '').trim(), (end || '').trim());
  };

  const doUpload = async (file, conference, conferenceStart, conferenceEnd) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/met/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data.rows?.length) {
        toast.error('No people found in that file');
      } else {
        setStaged({
          ...res.data,
          conference,
          conference_start: conferenceStart || '',
          conference_end: conferenceEnd || '',
        });
        toast.success(`Found ${res.data.total} people — review then commit to "${conference}"`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateStagedRow = (idx, patch) => {
    setStaged(prev => ({ ...prev, rows: prev.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  };
  const removeStagedRow = (idx) => {
    setStaged(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));
  };

  const commitStaged = async () => {
    if (!staged?.rows?.length) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/met/bulk`, {
        conference: staged.conference,
        conference_start: staged.conference_start || '',
        conference_end: staged.conference_end || '',
        rows: staged.rows.map(r => ({
          company_name: r.company_name || '',
          name: r.name || '',
          email: r.email || '',
          additional_emails: r.additional_emails || [],
          notes: r.notes || '',
        })),
      }, { withCredentials: true });
      toast.success(`Saved ${res.data.inserted} contacts to "${staged.conference}"`);
      setStaged(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const companyLink = (row) => {
    if (!row.linked_id) return null;
    if (row.linked_type === 'pharma') return `/company-profile/${row.linked_id}`;
    if (row.linked_type === 'investor') return `/investors/${row.linked_id}`;
    return `/biotech/${row.linked_id}`;
  };

  // ─────────── Manual "I've reached out to them" toggle ───────────
  // The user said they'd write/send emails directly from Gmail; this is
  // just the lightweight ledger that tracks who's been contacted.
  const toggleOutreached = async (row) => {
    const next = row.email_status === 'outreached' ? '' : 'outreached';
    const sentAt = next === 'outreached' ? new Date().toISOString() : '';
    // Optimistic update
    setRows(prev => prev.map(r => r.id === row.id ? { ...r, email_status: next, sent_at: sentAt } : r));
    try {
      await axios.put(`${API}/met/${row.id}`, {
        email_status: next, sent_at: sentAt,
      }, { withCredentials: true });
      toast.success(next === 'outreached'
        ? `Marked ${row.name || row.company_name} as reached out`
        : 'Reverted to not reached out');
    } catch (err) {
      // Roll back
      fetchRows();
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  // ─────────── Render ───────────
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="met-in-person-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#00709B]" /> Met In-Person
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Conference-specific meeting logs. Track who you've met and reached out to. Send the actual emails from your Gmail and mark them sent here.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,.xls,.pdf"
            className="hidden"
            onChange={(e) => onFilePicked(e.target.files?.[0])}
            data-testid="met-upload-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="met-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            New Conference Upload
          </Button>
          <Button
            onClick={() => {
              setShowAdd(v => !v);
              if (!form.conference && confNames[0]) {
                setForm(f => ({ ...f, conference: confNames[0] }));
              }
            }}
            className="bg-[#00709B] hover:bg-[#005a7a] text-white"
            data-testid="met-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Manually
          </Button>
          {stats.total > 0 && (
            <Button
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={clearAll}
              data-testid="met-clear-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Global stats */}
      <div className="flex items-center gap-2 text-xs" data-testid="met-stats-row">
        <Badge variant="secondary" className="bg-slate-100 text-slate-700" data-testid="met-stat-total">
          Total: {stats.total}
        </Badge>
        <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="met-stat-linked">
          Linked to CRM: {stats.matched}
        </Badge>
        <Badge variant="secondary" className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="met-stat-unmatched">
          Unmatched: {stats.unmatched}
        </Badge>
      </div>

      {/* Manual add card */}
      {showAdd && (
        <Card data-testid="met-add-form">
          <CardContent className="pt-5">
            <form onSubmit={submitAdd} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Conference folder *</label>
                <div className="flex items-center gap-2">
                  <Select
                    value={form.conference || ''}
                    onValueChange={(v) => setForm({ ...form, conference: v })}
                  >
                    <SelectTrigger className="flex-1" data-testid="met-add-conference-select">
                      <SelectValue placeholder="Pick or create a conference folder…" />
                    </SelectTrigger>
                    <SelectContent>
                      {confNames.map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                      <SelectItem value={NEW_CONF}>+ New folder…</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.conference === NEW_CONF && (
                    <Input
                      value={newConfName}
                      onChange={(e) => setNewConfName(e.target.value)}
                      placeholder="e.g. BIO China 2026"
                      className="flex-1"
                      data-testid="met-add-new-conf-input"
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Company name *</label>
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="e.g. Hansoh Pharma"
                  required
                  data-testid="met-add-company"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Person name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Dr Wei Zhang"
                  data-testid="met-add-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="wei.zhang@hansoh.com"
                  data-testid="met-add-email"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">
                  Additional company emails <span className="text-slate-400">(comma separated)</span>
                </label>
                <Input
                  value={form.additional_emails}
                  onChange={(e) => setForm({ ...form, additional_emails: e.target.value })}
                  placeholder="bd@hansoh.com, info@hansoh.com"
                  data-testid="met-add-additional"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Where you met, what was discussed, follow-up reminder…"
                  rows={2}
                  data-testid="met-add-notes"
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-2 justify-end pt-1">
                <Button type="button" variant="ghost" onClick={() => { setForm(emptyForm()); setShowAdd(false); }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#00709B] hover:bg-[#005a7a] text-white"
                  disabled={saving}
                  data-testid="met-add-submit"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Save Contact
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Staged upload — review before commit */}
      {staged && (
        <Card data-testid="met-staged-card" className="border-[#00709B]/30 ring-1 ring-[#00709B]/10">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <FileText className="w-4 h-4 text-[#00709B]" />
                Review {staged.rows.length} parsed rows →
                <Input
                  value={staged.conference}
                  onChange={(e) => setStaged({ ...staged, conference: e.target.value })}
                  className="h-7 text-sm w-56"
                  data-testid="met-staged-conf-input"
                />
                <span className="text-xs text-slate-500">
                  · {staged.rows.filter(r => r.match_status === 'linked').length} linked · {staged.rows.filter(r => r.match_status !== 'linked').length} new
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => setStaged(null)} data-testid="met-staged-cancel">
                  Discard
                </Button>
                <Button
                  className="bg-[#00709B] hover:bg-[#005a7a] text-white"
                  onClick={commitStaged}
                  disabled={saving || staged.rows.length === 0 || !staged.conference.trim()}
                  data-testid="met-staged-commit"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save All ({staged.rows.length})
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2.5 py-2 font-medium">Company</th>
                    <th className="text-left px-2.5 py-2 font-medium">Name</th>
                    <th className="text-left px-2.5 py-2 font-medium">Email</th>
                    <th className="text-left px-2.5 py-2 font-medium">Additional Emails</th>
                    <th className="text-left px-2.5 py-2 font-medium">Match</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {staged.rows.map((r, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-2 py-1.5"><Input className="h-7 text-xs" value={r.company_name || ''} onChange={(e) => updateStagedRow(idx, { company_name: e.target.value })} /></td>
                      <td className="px-2 py-1.5"><Input className="h-7 text-xs" value={r.name || ''} onChange={(e) => updateStagedRow(idx, { name: e.target.value })} /></td>
                      <td className="px-2 py-1.5"><Input className="h-7 text-xs" value={r.email || ''} onChange={(e) => updateStagedRow(idx, { email: e.target.value })} /></td>
                      <td className="px-2 py-1.5">
                        <Input className="h-7 text-xs"
                          value={(r.additional_emails || []).join(', ')}
                          onChange={(e) => updateStagedRow(idx, {
                            additional_emails: e.target.value.split(/[,;]/).map(s => s.trim()).filter(Boolean),
                          })}
                          placeholder="alt@…, bd@…" />
                      </td>
                      <td className="px-2 py-1.5">
                        {r.match_status === 'linked' ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]"
                            title={`${r.linked_type || 'biotech'} · ${(r.match_method || '').replace('_', ' ')}`}>
                            {r.linked_type === 'pharma' ? 'Pharma' : r.linked_type === 'investor' ? 'Investor' : 'Biotech'}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">New</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button onClick={() => removeStagedRow(idx)} className="text-slate-400 hover:text-red-500" title="Remove from list">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, name, or email…"
            className="pl-8 h-9 text-sm"
            data-testid="met-search-input"
          />
        </div>
        <div className="flex items-center gap-1 text-xs">
          <button onClick={() => setStatusFilter('')}
            className={`px-2.5 py-1 rounded-md border ${statusFilter === '' ? 'bg-[#00709B] text-white border-[#00709B]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid="met-filter-all">All</button>
          <button onClick={() => setStatusFilter('linked')}
            className={`px-2.5 py-1 rounded-md border ${statusFilter === 'linked' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid="met-filter-linked">Linked</button>
          <button onClick={() => setStatusFilter('unmatched')}
            className={`px-2.5 py-1 rounded-md border ${statusFilter === 'unmatched' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid="met-filter-unmatched">Unmatched</button>
        </div>
      </div>

      {/* Folders */}
      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-slate-400">
            No contacts yet. Upload a conference roster or click <span className="font-semibold text-slate-600">Add Manually</span>.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {conferences.map((conf) => {
            const isOpen = expanded[conf.name] !== false;
            const folderRows = grouped[conf.name] || [];
            const Icon = isOpen ? FolderOpen : Folder;
            return (
              <Card key={conf.name} data-testid={`met-folder-${conf.name}`}>
                <CardContent className="p-0">
                  {/* Folder header */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [conf.name]: !isOpen }))}
                      className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#00709B]"
                      data-testid={`met-folder-toggle-${conf.name}`}
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Icon className="w-4 h-4 text-[#00709B]" />
                      {renamingConf === conf.name ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitRenameConf(); if (e.key === 'Escape') setRenamingConf(null); }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 text-sm w-48"
                            placeholder="Conference name"
                            data-testid={`met-folder-rename-input-${conf.name}`}
                          />
                          <Input
                            type="date"
                            value={renameStart}
                            onChange={(e) => setRenameStart(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 text-xs w-36"
                            title="Start date (used in AI follow-ups)"
                          />
                          <span className="text-slate-400 text-xs">→</span>
                          <Input
                            type="date"
                            value={renameEnd}
                            onChange={(e) => setRenameEnd(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 text-xs w-36"
                            title="End date"
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); submitRenameConf(); }}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="flex items-center gap-2">
                          {conf.name}
                          {(conf.start_date || conf.end_date) && (
                            <span className="text-[10px] text-slate-400 font-normal">
                              {conf.start_date}{conf.end_date ? ` → ${conf.end_date}` : ''}
                            </span>
                          )}
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-white text-slate-600 border border-slate-200 text-[10px]">
                        {conf.total}
                      </Badge>
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">
                        {conf.matched} linked
                      </Badge>
                      {conf.unmatched > 0 && (
                        <Badge variant="secondary" className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">
                          {conf.unmatched} new
                        </Badge>
                      )}
                      <button
                        onClick={() => setBulkEmailFor({ name: conf.name, rows: folderRows })}
                        className="px-2 py-1 ml-1 rounded-md text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1"
                        title="Generate and send post-meeting emails to every linked contact in this folder"
                        data-testid={`met-folder-bulk-email-${conf.name}`}
                      >
                        <Sparkles className="w-3 h-3" /> Bulk post-meeting emails
                      </button>
                      {renamingConf !== conf.name && (
                        <>
                          <button
                            onClick={() => startRenameConf(conf)}
                            className="p-1.5 text-slate-400 hover:text-[#00709B] hover:bg-slate-100 rounded"
                            title="Rename folder + edit dates"
                            data-testid={`met-folder-rename-${conf.name}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteConf(conf.name, conf.total)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                            title="Delete folder + all contacts inside"
                            data-testid={`met-folder-delete-${conf.name}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Folder rows */}
                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`met-folder-table-${conf.name}`}>
                        <thead className="bg-white text-slate-500 text-[11px] uppercase tracking-wide">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Company</th>
                            <th className="text-left px-3 py-2 font-medium">Name</th>
                            <th className="text-left px-3 py-2 font-medium">Email</th>
                            <th className="text-left px-3 py-2 font-medium">Additional Emails</th>
                            <th className="text-left px-3 py-2 font-medium">Notes</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {folderRows.map((row) => {
                            const isEditing = editingId === row.id;
                            const link = companyLink(row);
                            return (
                              <React.Fragment key={row.id}>
                              <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`met-row-${row.id}`}>
                                {isEditing ? (
                                  <>
                                    <td className="px-2 py-1.5"><Input className="h-8 text-xs" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} /></td>
                                    <td className="px-2 py-1.5"><Input className="h-8 text-xs" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></td>
                                    <td className="px-2 py-1.5"><Input className="h-8 text-xs" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></td>
                                    <td className="px-2 py-1.5"><Input className="h-8 text-xs" value={editForm.additional_emails} onChange={(e) => setEditForm({ ...editForm, additional_emails: e.target.value })} /></td>
                                    <td className="px-2 py-1.5"><Input className="h-8 text-xs" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></td>
                                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                      <button onClick={() => saveEdit(row.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Save"><Check className="w-4 h-4" /></button>
                                      <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded" title="Cancel"><X className="w-4 h-4" /></button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-3 py-2">
                                      {link ? (
                                        <Link to={link} className="text-[#00709B] font-medium hover:underline inline-flex items-center gap-1 group"
                                          title={row.match_method ? `Linked via ${row.match_method.replace('_', ' ')}` : 'Linked'}
                                          data-testid={`met-company-link-${row.id}`}>
                                          {row.company_name}
                                          {row.linked_type && row.linked_type !== 'biotech' && (
                                            <span className="text-[9px] uppercase tracking-wide font-semibold text-slate-400">{row.linked_type}</span>
                                          )}
                                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </Link>
                                      ) : (
                                        <span className="text-slate-700 inline-flex items-center gap-1.5">
                                          {row.company_name}
                                          <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[9px] px-1.5 py-0">Not in CRM</Badge>
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">{row.name || <span className="text-slate-300">—</span>}</td>
                                    <td className="px-3 py-2 text-slate-600">
                                      {row.email ? <a href={`mailto:${row.email}`} className="hover:underline">{row.email}</a> : <span className="text-slate-300">—</span>}
                                      {(row.cc_emails || []).length > 0 && (
                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                          CC: {(row.cc_emails || []).join(', ')}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-500 text-xs">
                                      {(row.additional_emails || []).length === 0 ? <span className="text-slate-300">—</span> : (
                                        <div className="flex flex-wrap gap-1">
                                          {row.additional_emails.map((e, i) => (
                                            <a key={i} href={`mailto:${e}`} className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200">{e}</a>
                                          ))}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-500 text-xs max-w-[260px] truncate" title={row.notes}>
                                      {row.notes || <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                      <button
                                        onClick={() => toggleOutreached(row)}
                                        className={
                                          row.email_status === 'outreached'
                                            ? "px-2 py-1 mr-1 rounded-md text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                            : "px-2 py-1 mr-1 rounded-md text-[10px] font-medium bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"
                                        }
                                        title={
                                          row.email_status === 'outreached'
                                            ? `Outreached${row.sent_at ? ' on ' + row.sent_at.slice(0, 10) : ''} — click to revert`
                                            : 'Click after you send a follow-up email from Gmail'
                                        }
                                        data-testid={`met-toggle-outreached-${row.id}`}
                                      >
                                        {row.email_status === 'outreached' ? (
                                          <span className="inline-flex items-center gap-1">
                                            <MailCheck className="w-3 h-3" />
                                            {row.sent_at ? row.sent_at.slice(0, 10) : 'Sent'}
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 text-slate-400">
                                            <Mail className="w-3 h-3" />
                                            Mark sent
                                          </span>
                                        )}
                                      </button>
                                      <button onClick={() => startEdit(row)} className="p-1 text-slate-400 hover:text-[#00709B] hover:bg-slate-100 rounded" title="Edit" data-testid={`met-edit-${row.id}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                      <PostMeetingEmailButton row={row} onSent={fetchRows} />
                                      <button onClick={() => deleteRow(row.id, row.name || row.company_name)} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete" data-testid={`met-delete-${row.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </td>
                                  </>
                                )}
                              </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Conference details dialog — replaces the old window.prompt() chain */}
      <Dialog
        open={confDialog.open}
        onOpenChange={(o) => { if (!o) cancelConfDialog(); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="met-conf-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#00709B]" />
              Conference details
            </DialogTitle>
            <DialogDescription>
              Help the AI write natural follow-ups by tagging this upload with the
              conference name and dates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="met-conf-name" className="text-xs font-medium text-slate-600">
                Conference / event name *
              </Label>
              <Input
                id="met-conf-name"
                value={confDialog.conference}
                onChange={e => setConfDialog(p => ({ ...p, conference: e.target.value }))}
                placeholder="e.g. BIO China 2026"
                autoFocus
                data-testid="met-conf-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="met-conf-start" className="text-xs font-medium text-slate-600">
                  Start date <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="met-conf-start"
                  type="date"
                  value={confDialog.start}
                  onChange={e => setConfDialog(p => ({ ...p, start: e.target.value }))}
                  data-testid="met-conf-start-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="met-conf-end" className="text-xs font-medium text-slate-600">
                  End date <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="met-conf-end"
                  type="date"
                  value={confDialog.end}
                  min={confDialog.start || undefined}
                  onChange={e => setConfDialog(p => ({ ...p, end: e.target.value }))}
                  data-testid="met-conf-end-input"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Dates are optional — leave blank if you're not sure. You can always rename or
              re-date this conference later from the folder header.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={cancelConfDialog} data-testid="met-conf-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitConfDialog}
              className="bg-[#00709B] hover:bg-[#005a7a] text-white"
              disabled={!confDialog.conference.trim()}
              data-testid="met-conf-submit"
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkPostMeetingEmail
        conferenceName={bulkEmailFor?.name || ''}
        rows={bulkEmailFor?.rows || []}
        open={!!bulkEmailFor}
        onClose={() => setBulkEmailFor(null)}
        onSent={fetchRows}
      />
    </div>
  );
}
